import { Firebase } from "@/firebase";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

function getCallable(functionName) {
  return Firebase.app().functions("europe-west2").httpsCallable(functionName);
}

async function callFunction(functionName, payload) {
  const callable = getCallable(functionName);
  const result = await callable(payload || {});
  return result && result.data ? result.data : {};
}

function triggerDownload(url, filename) {
  if (!url) throw new Error("Missing download URL");
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  if (filename) link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function runZipJob({ createPayload, onProgress, onCachedResult }) {
  const payload = { ...(createPayload || {}) };
  const created = await callFunction("createZipJob", payload);
  if (typeof onProgress === "function") {
    onProgress({
      status: created.status || "queued",
      stage: created.stage || "queued",
      progress: typeof created.progress === "number" ? created.progress : 0,
      cached: Boolean(created.cached),
    });
  }

  if (created.cached && created.status === "ready" && created.downloadUrl && typeof onCachedResult === "function") {
    const decision = await onCachedResult(created);
    if (decision === "rebuild") {
      return runZipJob({
        createPayload: { ...(payload || {}), forceRebuild: true },
        onProgress,
        onCachedResult,
      });
    }
  }

  if (created.status === "ready" && created.downloadUrl) {
    triggerDownload(created.downloadUrl, created.outputFileName || null);
    return created;
  }

  const startedAt = Date.now();
  while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    const status = await callFunction("getZipJobStatus", {
      jobId: created.jobId,
      jobToken: created.jobToken,
    });

    if (typeof onProgress === "function") {
      onProgress({
        status: status.status || "queued",
        stage: status.stage || "",
        progress: typeof status.progress === "number" ? status.progress : 0,
      });
    }

    if (status.status === "ready") {
      triggerDownload(status.downloadUrl, status.outputFileName || null);
      return status;
    }
    if (status.status === "failed") {
      throw new Error(status.error || "Zip generation failed");
    }
  }

  throw new Error("Zip job timed out");
}
